import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import DataTable from "react-data-table-component";
import { useManageIPMSContext } from "./ManageIPMSContex";
import { ChangeApprovalEmployeeListResponse } from "./ipmsmodel";

import useIPMSApi from "./ipms";
import { toast } from "react-toastify";
import SearchUserName from "../../components/adminSearchUserName";

const EditableCell = ({
  row,
  employee_id,
  onSave,
}: {
  row: ChangeApprovalEmployeeListResponse;
  employee_id: number | null;
  onSave: (dates: string, value: string) => void;
}) => {
  const { saveChangeAppriser } = useIPMSApi();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(
    row.manager_name !== "N/A" ? row.manager_name : ""
  );
  const [appriseValue, setAppriseValue] = useState<number | null>(null);

  const handleSave = useCallback(async () => {
    if (value && !appriseValue) {
      setIsEditing(false);
      return;
    }
    try {
      const assessment_year = Number(row?.start_year);
      const quarter_number = row?.quarter;

      if (!employee_id) {
        toast.error("Employee ID is missing.");
        return;
      }

      if (!appriseValue) {
        toast.error("Please select a valid manager.");
        return;
      }

      const response = await saveChangeAppriser(
        employee_id,
        assessment_year,
        quarter_number,
        appriseValue
      );
      if (response) {
        onSave(row.quarter_dates, value);
        setIsEditing(false);
      }
      return response;
    } catch (error) {
      // Handle error
    }
  }, [saveChangeAppriser]);

  useEffect(() => {
    // Update the value if row.manager_name changes (this will re-render the component)
    setValue(row.manager_name);
  }, [row.manager_name]); // Dependency on row.manager_name ensures re-render

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2">
        <SearchUserName
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          centerAlign={false}
          setOptionsData={(item) => {
            setValue(item?.ldap_uid ?? "");
            setAppriseValue(item?.employee_id ?? null);
          }}
        />
        <button className="text-blue-500" onClick={handleSave}>
          Save
        </button>
        <button className="text-red-500" onClick={() => setIsEditing(false)}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <span>{row.manager_name}</span>
      <button className="h-6 w-6" onClick={() => setIsEditing(true)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-pencil"
        >
          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          <path d="m15 5 4 4" />
        </svg>
      </button>
    </div>
  );
};

export default function AssignManageable({
  employeeId,
}: {
  employeeId: number | null;
}) {
  const [filterText, setFilterText] = useState("");
  const { QuarterAppriserEmployeeList } =
    useManageIPMSContext();

  console.log("QuarterAppriserEmployeeList", QuarterAppriserEmployeeList);
  const [tableData, setTableData] = useState<
    ChangeApprovalEmployeeListResponse[] | null
  >();

  useEffect(() => {
    setTableData(QuarterAppriserEmployeeList);
  }, [QuarterAppriserEmployeeList]);

  const customStyles = {
    rows: {
      style: {
        minHeight: "45px", // Set the minimum height for rows
      },
    },
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "#f3f4f6", // Tailwind gray-100
        fontWeight: "bold",
        fontSize: "0.9rem", // Adjust font size as needed
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        padding: "8px",
        // Additional styles can be added here
      },
    },
  };

  // Apply column-specific styles if cellClass is defined
  const getCustomColumnStyles = (columnName: string) => {
    return {
      style: {
        // Apply specific styles based on the column
        ...(columnName && { whiteSpace: "normal" }), // Example for 'id' column
      },
    };
  };

  // Create column styles based on the column width and custom classes
  const getColumnStyles = () => {
    return columns.reduce((styles, column) => {
      if ("width" in column && column.width) {
        styles[column.name] = getCustomColumnStyles(column.name);
      }
      return styles;
    }, {} as Record<string, { style: Record<string, string> }>);
  };

  const filteredData = useMemo(() => {
    return (
      tableData?.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(filterText.toLowerCase())
        )
      ) || []
    );
  }, [tableData, filterText]);

  const handleManagerUpdate = useCallback(
    (dates: string, newManagerName: string) => {
      console.log("Updating manager:", dates, newManagerName);

      // Create a new array with the updated manager name
      const updatedData = tableData?.map((item) => {
        if (item.quarter_dates === dates) {
          return { ...item, manager_name: newManagerName };
        }
        return item;
      });

      // Update the state with the new data
      setTableData(updatedData ?? null);
    },
    [tableData]
  );

  // Column definitions
  const columns = useMemo(() => {
    return [
      {
        name: "Quarter",
        selector: (row: ChangeApprovalEmployeeListResponse) =>
          "Q" + row.quarter,
        sortable: true,
      },
      {
        name: "Year",
        selector: (row: ChangeApprovalEmployeeListResponse) => row.start_year,
        sortable: true,
      },
      {
        name: "Dates",
        selector: (row: ChangeApprovalEmployeeListResponse) =>
          row.quarter_dates,
        sortable: true,
      },
      {
        name: "Manager Name",
        cell: (row: ChangeApprovalEmployeeListResponse) => (
          <EditableCell
            employee_id={employeeId}
            row={row}
            onSave={handleManagerUpdate}
          />
        ),
        sortable: true,
        selector: (row: ChangeApprovalEmployeeListResponse) => row.manager_name,
      },
    ];
  }, [employeeId, QuarterAppriserEmployeeList, handleManagerUpdate]); // Dependencies

  return (
    <div className="px-2 rounded-md overflow-hidden">
      <div className="max-w-lg py-4 lg:flex justify-center">
        <div className="w-full mr-2 ">
          <input
            type="text"
            placeholder={"Search..."}
            value={filterText}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFilterText(e.target.value)
            }
            className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        customStyles={{ ...customStyles, ...getColumnStyles() }}
        highlightOnHover
        pointerOnHover
        responsive
      />
    </div>
  );
}
